import { MeetingNoteTense, TemplateCreationData, TemplateType } from '../types';
import { UploadedTemplate } from 'src/data-capture/hooks/templates/useTemplates';
import { useState } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { Logger } from 'src/utils/Logger';

type UUID = string;
type CreateTemplatePayload = {
  displayName: string;
  description: string;
  examplePopulatedFileId: UUID;
  exampleTemplatedFileId: UUID;
  supportsMulticlient: boolean;
  eligibleForSummarisation: boolean;
  templateType: TemplateType;
  previewClientId: string | null;
  sections: CreateSectionPayload[];
  markdownImageCollectionId: string | null;
  meetingNoteTense: MeetingNoteTense;
};
type CreateSectionPayload = {
  populatedMarkdown: string;
  templatedMarkdown: string;
  sectionHeader: string;
  sectionHeaderLevel: number;
  preserveAsIs: boolean;
};

export const useCreateTemplateStream = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<string>('');

  const createTemplateStream = async (data: TemplateCreationData): Promise<UploadedTemplate> => {
    setIsLoading(true);
    setProgress('Starting template creation...');

    const payload: CreateTemplatePayload = {
      displayName: data.templateName,
      description: data.templateDescription ?? '',
      templateType: data.templateType!,
      examplePopulatedFileId: data.examplePopulatedFile!.id,
      exampleTemplatedFileId: data.exampleTemplateFile!.id,
      supportsMulticlient: false,
      eligibleForSummarisation: data.eligibleForSummarisation ?? true,
      previewClientId: data.previewClientId ?? null,
      sections: data.templateSections.map(section => ({
        populatedMarkdown: section.populatedMarkdown ?? '',
        templatedMarkdown: section.templatedMarkdown,
        sectionHeader: section.sectionHeader,
        sectionHeaderLevel: section.sectionHeaderLevel,
        preserveAsIs: section.preserveAsIs,
      })),
      markdownImageCollectionId: data.extractedImagesCollection?.id ?? null,
      meetingNoteTense: data.meetingNoteTense ?? 'THIRD_PERSON',
    };

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

    try {
      const response = await fetch(
        `${API_URL}/admin/companies/${data.company.id}/templates/stream`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to create template: ${response.status}`);
      }

      return await handleStreamResponse(response);
    } catch (error) {
      setIsLoading(false);
      setProgress('');
      throw error;
    }
  };

  const handleStreamResponse = async (response: Response): Promise<UploadedTemplate> => {
    if (!response.body) throw new Error('No response body');

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .pipeThrough(new EventSourceParserStream())
      .getReader();

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done || value.data === '\n\n') break;

        if (value.data) {
          try {
            const parsed = JSON.parse(value.data);

            if (parsed.type === 'progress') {
              setProgress(parsed.data.message);
            } else if (parsed.type === 'complete') {
              setIsLoading(false);
              setProgress('');
              return parsed.data.template;
            }
          } catch (e) {
            Logger.warn('Parse error', { error: e, data: value.data });
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    throw new Error('Stream ended without completion');
  };

  return { createTemplateStream, isLoading, progress };
};
